import type { VisualRandom } from '../core/VisualRandom.js';
import type { VisualSignalFrame } from '../signals/VisualSignal.js';

/**
 * Which material a wave comes out as.
 *
 * Kept apart from `SpatialPlanner` on purpose: *what* is drawn and *where* it is
 * drawn are two independent decisions, and a run where the material changes but
 * the position does not is as flat as one where neither does. The engine asks
 * this for a material, asks the planner for a place, and puts the two together.
 *
 * `available` is whatever renderer ids the host registered. This file never
 * names a renderer — a build without `WATERCOLOR` gets a weighting table with
 * `WATERCOLOR` in it and simply never picks it, rather than crashing or drawing
 * nothing.
 */

export const WAVE_MODES = [
  'FIXED',
  'RANDOM',
  'WEIGHTED_RANDOM',
  'SHUFFLE',
  'AUDIO_REACTIVE',
  'COLLISION',
] as const;
export type WaveMode = (typeof WAVE_MODES)[number];

export function isWaveMode(value: string): value is WaveMode {
  return (WAVE_MODES as readonly string[]).includes(value);
}

/** Relative weights by renderer id. Zero and negative mean "never". */
export type WaveStyleWeights = Readonly<Record<string, number>>;

export const DEFAULT_WAVE_WEIGHTS: WaveStyleWeights = {
  SMOKE: 30,
  RIPPLE: 25,
  CODE: 15,
  MATH: 10,
  SUMI: 10,
  WATERCOLOR: 10,
};

export interface WaveDirectorOptions {
  readonly mode: WaveMode;
  /** Materials this build can actually draw. */
  readonly available: readonly string[];
  readonly weights?: WaveStyleWeights;
  /** `FIXED` draws this one. Defaults to the first available. */
  readonly fixed?: string;
  /** COLLISION only: how many materials may answer one disturbance. */
  readonly minMaterials?: number;
  readonly maxMaterials?: number;
}

/**
 * Pairings worth reaching for.
 *
 * COLLISION could draw its materials uniformly and would produce mush: six
 * materials taken three at a time is twenty combinations, most of which are
 * three unrelated things in the same place. These are the ones where the two
 * materials have something to say to each other — smoke carrying text,
 * waves starting from an ink edge, formulae laid along a wash, code showing
 * only where paint has been. They are *preferred*, not required: the seed
 * still decides, and a build without one half falls back to a free draw.
 */
export const AFFINITIES: readonly (readonly [string, string])[] = [
  ['SMOKE', 'CODE'],
  ['SUMI', 'RIPPLE'],
  ['WATERCOLOR', 'MATH'],
  ['INK_PAINT', 'CODE'],
  ['SMOKE', 'SUMI'],
  ['RIPPLE', 'WATERCOLOR'],
  ['INK_PAINT', 'SMOKE'],
];

export class WaveDirector {
  private mode: WaveMode;
  private available: readonly string[];
  private weights: WaveStyleWeights;
  private fixed: string | null;
  private minMaterials: number;
  private maxMaterials: number;

  /** What `SHUFFLE` has not used yet in the current pass. */
  private bag: string[] = [];
  private readonly counts: Record<string, number> = {};
  private chosen = 0;
  /** Favoured until the next section. See `retune`. */
  private favoured: string | null = null;
  private sections = 0;
  private collisions = 0;

  constructor(options: WaveDirectorOptions) {
    this.mode = options.mode;
    this.available = [...options.available];
    this.weights = options.weights ?? DEFAULT_WAVE_WEIGHTS;
    this.fixed = options.fixed ?? null;
    this.minMaterials = Math.max(1, options.minMaterials ?? 1);
    this.maxMaterials = Math.max(this.minMaterials, options.maxMaterials ?? 3);
  }

  configure(options: Partial<WaveDirectorOptions>): void {
    if (options.mode !== undefined) this.mode = options.mode;
    if (options.available !== undefined) this.available = [...options.available];
    if (options.weights !== undefined) this.weights = options.weights;
    if (options.fixed !== undefined) this.fixed = options.fixed;
    if (options.minMaterials !== undefined) this.minMaterials = Math.max(1, options.minMaterials);
    if (options.maxMaterials !== undefined) {
      this.maxMaterials = Math.max(this.minMaterials, options.maxMaterials);
    }
    // The bag names materials that may no longer exist.
    this.bag = [];
  }

  reset(): void {
    this.bag = [];
    this.chosen = 0;
    this.favoured = null;
    this.sections = 0;
    this.collisions = 0;
    for (const key of Object.keys(this.counts)) delete this.counts[key];
  }

  /** Materials with a positive weight that this build can draw, in order. */
  private get playable(): readonly string[] {
    const weighted = this.available.filter((id) => (this.weights[id] ?? 0) > 0);
    // A weighting table that excludes everything this build has is a
    // configuration mistake, not a reason to draw nothing.
    return weighted.length > 0 ? weighted : this.available;
  }

  /**
   * The section changed, so the tendency does.
   *
   * One material is favoured until the next section, and the shuffle bag is
   * emptied so a new pass starts. This is what makes a section boundary
   * legible without changing what any single wave is allowed to be: the
   * *distribution* moves, not the vocabulary.
   */
  retune(random: VisualRandom): void {
    this.bag = [];
    const playable = this.playable;
    if (playable.length === 0) return;
    this.favoured = playable[random.int(0, playable.length)] as string;
    this.sections += 1;
  }

  /**
   * Every material that answers one disturbance.
   *
   * One entry outside COLLISION, so a caller can always use this and the
   * five older modes keep behaving exactly as they did. Inside COLLISION the
   * count is drawn first, then a lead material, then partners — biased
   * towards the affinities above, because three materials picked at random
   * is not a collision, it is a pile.
   */
  chooseMany(random: VisualRandom, signals: VisualSignalFrame): readonly string[] {
    if (this.mode !== 'COLLISION') {
      const single = this.choose(random, signals);
      return single === null ? [] : [single];
    }

    const playable = this.playable;
    if (playable.length === 0) return [];

    const most = Math.min(this.maxMaterials, playable.length);
    const count = this.minMaterials >= most
      ? most
      : random.int(this.minMaterials, most + 1);

    // The lead is weighted, so a recipe that mostly wants smoke mostly gets
    // collisions led by smoke.
    const picked: string[] = [];
    const lead = this.weightedPick(playable, random);
    picked.push(lead);
    this.note(lead);

    while (picked.length < count) {
      const partner = this.partnerFor(picked, playable, random);
      if (partner === null) break;
      picked.push(partner);
      this.note(partner);
    }
    this.collisions += 1;
    return picked;
  }

  /**
   * A material to join the ones already picked.
   *
   * Prefers something with an affinity to any of them; failing that, takes a
   * weighted draw from whatever is left. A draw is taken on both paths so
   * that the stream advances by the same amount either way — otherwise a
   * build missing one material would desynchronise every later decision.
   */
  private partnerFor(
    chosen: readonly string[],
    playable: readonly string[],
    random: VisualRandom,
  ): string | null {
    const free = playable.filter((id) => !chosen.includes(id));
    if (free.length === 0) return null;

    const affine = free.filter((id) =>
      AFFINITIES.some(
        ([a, b]) =>
          (a === id && chosen.includes(b)) || (b === id && chosen.includes(a)),
      ),
    );
    const roll = random.next();
    // Two draws in three go to a pairing that means something; the third is
    // free, so a run of collisions does not become predictable.
    const pool = affine.length > 0 && roll < 0.68 ? affine : free;
    return pool[random.int(0, pool.length)] as string;
  }

  private weightedPick(playable: readonly string[], random: VisualRandom): string {
    let total = 0;
    for (const id of playable) total += this.weightOf(id);
    if (total <= 0) return random.pick(playable);
    let roll = random.next() * total;
    for (const id of playable) {
      roll -= this.weightOf(id);
      if (roll <= 0) return id;
    }
    return playable[playable.length - 1] as string;
  }

  private note(id: string): void {
    this.counts[id] = (this.counts[id] ?? 0) + 1;
    this.chosen += 1;
  }

  choose(random: VisualRandom, signals: VisualSignalFrame): string | null {
    const playable = this.playable;
    if (playable.length === 0) return null;

    const picked = this.pick(playable, random, signals);
    this.counts[picked] = (this.counts[picked] ?? 0) + 1;
    this.chosen += 1;
    return picked;
  }

  private pick(
    playable: readonly string[],
    random: VisualRandom,
    signals: VisualSignalFrame,
  ): string {
    const first = playable[0] as string;

    switch (this.mode) {
      case 'FIXED': {
        if (this.fixed !== null && this.available.includes(this.fixed)) return this.fixed;
        return first;
      }

      case 'RANDOM':
        return random.pick(playable);

      case 'WEIGHTED_RANDOM': {
        let total = 0;
        for (const id of playable) total += this.weightOf(id);
        if (total <= 0) return random.pick(playable);
        let roll = random.next() * total;
        for (const id of playable) {
          roll -= this.weightOf(id);
          if (roll <= 0) return id;
        }
        // Only reachable through floating-point drift at the very top of the
        // range, and the last entry is the one that range belongs to.
        return playable[playable.length - 1] as string;
      }

      case 'SHUFFLE': {
        // Every material gets a turn before any gets a second. Plain RANDOM
        // will happily draw smoke four times running, and a mode whose whole
        // claim is variety cannot afford that.
        if (this.bag.length === 0) this.bag = this.shuffled(playable, random);
        return this.bag.pop() as string;
      }

      case 'AUDIO_REACTIVE':
        return this.byEar(playable, random, signals);

      default:
        // COLLISION, asked for a single material — by a caller using the old
        // `choose`. The lead of a collision is a weighted draw, so this is
        // that, and the mode degrades to WEIGHTED_RANDOM rather than to an
        // error nobody would see.
        return this.weightedPick(playable, random);
    }
  }

  /**
   * The music picks.
   *
   * Deliberately a small set of rules rather than a classifier. Each band gets
   * the materials whose behaviour matches what that part of the sound does:
   * bass is weight and volume, so it is smoke; treble is detail and edges, so it
   * is text and wave crests; a quiet passage is where the slow materials have
   * time to spread, so it is ink and pigment; an onset is an impact.
   *
   * Falls back through the list, so a build missing the first choice still
   * answers the *sense* of the rule rather than dropping to a default.
   */
  private byEar(
    playable: readonly string[],
    random: VisualRandom,
    signals: VisualSignalFrame,
  ): string {
    const bass = level(signals.BASS);
    const treble = level(signals.TREBLE);
    const amplitude = level(signals.AMPLITUDE);
    const onset = level(signals.ONSET);

    const order: string[] = [];
    if (onset > 0.6) order.push('RIPPLE', 'SMOKE');
    if (bass > 0.55 && bass >= treble) order.push('SMOKE', 'RIPPLE');
    if (treble > 0.5 && treble > bass) order.push('CODE', 'MATH', 'RIPPLE');
    // Quiet has to mean the *whole* frame is quiet. Testing the amplitude on
    // its own matched a heavy bass passage as well, because a band can be loud
    // while the mixed amplitude reads low — so a bass drop was offered smoke
    // and ink at the same time and the rule decided nothing.
    if (amplitude < 0.25 && bass < 0.4 && treble < 0.4) order.push('SUMI', 'WATERCOLOR');
    // Nothing stood out. Rather than always answering the same way, take the
    // loudest band's family and let the seed choose within it.
    if (order.length === 0) {
      order.push(...(bass >= treble ? ['SMOKE', 'SUMI'] : ['RIPPLE', 'CODE']));
    }

    const candidates = order.filter((id) => playable.includes(id));
    if (candidates.length === 0) return random.pick(playable);
    // A draw is still taken when there is only one candidate, so that the number
    // of draws per event does not depend on which branch fired — otherwise the
    // stream would desynchronise between two runs that heard the music
    // fractionally differently.
    return candidates[random.int(0, candidates.length)] as string;
  }

  /** A material's weight, with this section's favourite counted double. */
  private weightOf(id: string): number {
    const base = Math.max(0, this.weights[id] ?? 0);
    return id === this.favoured ? base * 2 : base;
  }

  /** Fisher-Yates, from the supplied stream. Popped from the end. */
  private shuffled(values: readonly string[], random: VisualRandom): string[] {
    const out = [...values];
    for (let index = out.length - 1; index > 0; index -= 1) {
      const swap = random.int(0, index + 1);
      const held = out[index] as string;
      out[index] = out[swap] as string;
      out[swap] = held;
    }
    return out;
  }

  diagnostics(): {
    readonly mode: WaveMode;
    readonly available: readonly string[];
    readonly playable: readonly string[];
    readonly chosen: number;
    readonly counts: Readonly<Record<string, number>>;
    readonly bagRemaining: number;
    readonly favoured: string | null;
    readonly sections: number;
    readonly collisions: number;
    readonly materialRange: readonly [number, number];
  } {
    return {
      mode: this.mode,
      available: [...this.available],
      playable: [...this.playable],
      chosen: this.chosen,
      counts: { ...this.counts },
      bagRemaining: this.bag.length,
      favoured: this.favoured,
      sections: this.sections,
      collisions: this.collisions,
      materialRange: [this.minMaterials, this.maxMaterials],
    };
  }
}

function level(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
